/** Pixel dust and a soft box of light. No images. Kept dim so the town stays gritty. */

export type Blend = 'source-over' | 'lighter';

export type GlowSpec = {
  x: number;
  y: number;
  rx: number;
  ry: number;
  rgb: [number, number, number];
  alpha: number;
  blend: Blend;
};

export type EmitSpec = {
  id: string;
  x: number;
  y: number;
  /** Particles per second. */
  rate: number;
  life: number;
  speed: number;
  rise: number;
  spread: number;
  rgb: [number, number, number];
  size: number;
  blend: Blend;
};

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  rgb: [number, number, number];
  size: number;
  blend: Blend;
};

const pool: Particle[] = [];
const bank = new Map<string, number>();
const CAP = 140;
let lastTime = 0;

export function resetFx(): void {
  pool.length = 0;
  bank.clear();
  lastTime = 0;
}

export function fxCount(): number {
  return pool.length;
}

const BURST: Record<string, { n: number; speed: number; life: number; rgb: [number, number, number] }> = {
  spark: { n: 10, speed: 28, life: 0.28, rgb: [127, 220, 210] },
  laser: { n: 8, speed: 22, life: 0.22, rgb: [244, 241, 234] },
  pop: { n: 16, speed: 46, life: 0.4, rgb: [224, 122, 61] },
  bell: { n: 14, speed: 34, life: 0.45, rgb: [224, 210, 160] },
  suck: { n: 14, speed: 36, life: 0.45, rgb: [176, 140, 220] },
  death: { n: 12, speed: 30, life: 0.45, rgb: [180, 160, 130] },
  loot: { n: 14, speed: 26, life: 0.6, rgb: [224, 177, 90] },
};

/** A one-shot spray. Bigger and brighter than the town dust. */
export function burstFx(x: number, y: number, kind: string): void {
  const spec = BURST[kind] ?? BURST.spark;
  const inward = kind === 'suck';
  for (let i = 0; i < spec.n && pool.length < CAP; i++) {
    const angle = (Math.PI * 2 * i) / spec.n + (Math.random() - 0.5) * 0.4;
    const speed = spec.speed * (0.45 + Math.random() * 0.7);
    const life = spec.life * (0.7 + Math.random() * 0.45);
    const spawn = inward ? 10 + Math.random() * 8 : Math.random() * 2;
    pool.push({
      x: x + Math.cos(angle) * spawn,
      y: y + Math.sin(angle) * spawn,
      vx: Math.cos(angle) * speed * (inward ? -1 : 1),
      vy: Math.sin(angle) * speed * (inward ? -1 : 1),
      life,
      max: life,
      rgb: spec.rgb,
      size: 2,
      blend: 'lighter',
    });
  }
}

export function stepFx(time: number, emitters: EmitSpec[], reduce: boolean): void {
  if (reduce) {
    pool.length = 0;
    bank.clear();
    lastTime = time;
    return;
  }
  const dt = lastTime === 0 ? 0.016 : Math.min(0.05, Math.max(0, time - lastTime));
  lastTime = time;
  const live = new Set(emitters.map((item) => item.id));
  for (const id of bank.keys()) if (!live.has(id)) bank.delete(id);
  for (const spec of emitters) {
    let pending = (bank.get(spec.id) ?? 0) + spec.rate * dt;
    while (pending >= 1 && pool.length < CAP) {
      pending -= 1;
      const angle = (Math.random() - 0.5) * spec.spread;
      const life = spec.life * (0.65 + Math.random() * 0.5);
      pool.push({
        x: spec.x + (Math.random() - 0.5) * 3,
        y: spec.y,
        vx: Math.sin(angle) * spec.speed,
        vy: -spec.rise - Math.abs(Math.cos(angle)) * spec.speed * 0.35,
        life,
        max: life,
        rgb: spec.rgb,
        size: spec.size,
        blend: spec.blend,
      });
    }
    if (pool.length >= CAP) pending = Math.min(pending, 1);
    bank.set(spec.id, pending);
  }
  for (let i = pool.length - 1; i >= 0; i--) {
    const speck = pool[i];
    speck.life -= dt;
    if (speck.life <= 0) {
      pool.splice(i, 1);
      continue;
    }
    speck.x += speck.vx * dt;
    speck.y += speck.vy * dt;
  }
}

export function drawGlow(ctx: CanvasRenderingContext2D, glows: GlowSpec[]): void {
  for (const glow of glows) {
    ctx.save();
    ctx.globalCompositeOperation = glow.blend;
    const rings = [1, 0.5, 0.22];
    for (let i = 0; i < rings.length; i++) {
      const rx = Math.max(1, Math.round(glow.rx * (1 - i * 0.28)));
      const ry = Math.max(1, Math.round(glow.ry * (1 - i * 0.28)));
      ctx.fillStyle = `rgba(${glow.rgb[0]},${glow.rgb[1]},${glow.rgb[2]},${(glow.alpha * rings[i]).toFixed(3)})`;
      ctx.fillRect(Math.round(glow.x - rx), Math.round(glow.y - ry), rx * 2, ry * 2);
    }
    ctx.restore();
  }
}

export function drawParticles(ctx: CanvasRenderingContext2D): void {
  for (const blend of ['source-over', 'lighter'] as const) {
    let any = false;
    for (const speck of pool) if (speck.blend === blend) any = true;
    if (!any) continue;
    ctx.save();
    ctx.globalCompositeOperation = blend;
    for (const speck of pool) {
      if (speck.blend !== blend) continue;
      const fade = Math.max(0, speck.life / speck.max);
      ctx.fillStyle = `rgba(${speck.rgb[0]},${speck.rgb[1]},${speck.rgb[2]},${(fade * 0.8).toFixed(3)})`;
      ctx.fillRect(Math.round(speck.x), Math.round(speck.y), speck.size, speck.size);
    }
    ctx.restore();
  }
}

let dark: HTMLCanvasElement | null = null;

export type DarkView = { dpr: number; scale: number; camX: number; camY: number; time: number; reduce: boolean; you: { x: number; y: number } };

function punch(g: CanvasRenderingContext2D, sx: number, sy: number, r: number, strength: number): void {
  const grad = g.createRadialGradient(sx, sy, 0, sx, sy, r);
  const stops: [number, number][] = [[0, 1], [0.3, 0.85], [0.6, 0.45], [0.85, 0.12], [1, 0]];
  for (const [at, a] of stops) grad.addColorStop(at, `rgba(0,0,0,${(a * strength).toFixed(3)})`);
  g.fillStyle = grad;
  g.fillRect(sx - r, sy - r, r * 2, r * 2);
}

const DARK_ALPHA = 0.93;
/** Fewer levels is chunkier. The dark is drawn one world pixel per pixel, then stretched up. */
const DITHER_LEVELS = 8;
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

/** Night over everything, with soft dithered holes for the player and every light source, then a little colour on top. Call in world space, after the scene. */
export function drawDarkness(ctx: CanvasRenderingContext2D, v: DarkView, lights: GlowSpec[]): void {
  const k = v.dpr * v.scale;
  const lw = Math.ceil(ctx.canvas.width / k) + 2;
  const lh = Math.ceil(ctx.canvas.height / k) + 2;
  if (!dark) dark = document.createElement('canvas');
  if (dark.width !== lw || dark.height !== lh) {
    dark.width = lw;
    dark.height = lh;
  }
  const g = dark.getContext('2d', { willReadFrequently: true });
  if (!g) return;
  const ax = Math.floor(v.camX);
  const ay = Math.floor(v.camY);
  g.globalCompositeOperation = 'source-over';
  g.fillStyle = `rgba(6,5,12,${DARK_ALPHA})`;
  g.fillRect(0, 0, lw, lh);
  g.globalCompositeOperation = 'destination-out';
  const flicker = v.reduce ? 0 : Math.sin(v.time * 9) * 0.015 + Math.sin(v.time * 5.3) * 0.015;
  punch(g, v.you.x - ax, v.you.y - 8 - ay, 265 * (1 + flicker), 1);
  for (const light of lights) {
    const r = Math.max(light.rx, light.ry) * 3.2 + 10;
    const wobble = v.reduce ? 1 : 1 + Math.sin(v.time * 7 + light.x * 0.37) * 0.04;
    punch(g, light.x - ax, light.y - ay, r * wobble, Math.min(1, light.alpha * 5 + 0.2));
  }
  // Ordered dither, pinned to world pixels so it does not swim when the camera moves.
  const img = g.getImageData(0, 0, lw, lh);
  const px = img.data;
  const steps = DITHER_LEVELS - 1;
  for (let y = 0; y < lh; y++) {
    for (let x = 0; x < lw; x++) {
      const at = (y * lw + x) * 4;
      const t = (BAYER[((x + ax) & 3) + (((y + ay) & 3) << 2)] + 0.5) / 16;
      const q = Math.floor((px[at + 3] / 255 / DARK_ALPHA) * steps + t) / steps;
      px[at] = 6;
      px[at + 1] = 5;
      px[at + 2] = 12;
      px[at + 3] = Math.round(q * DARK_ALPHA * 255);
    }
  }
  g.putImageData(img, 0, 0);

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(dark, 0, 0, lw, lh, (ax - v.camX) * k, (ay - v.camY) * k, lw * k, lh * k);
  // Colour: each source stains the ground around it, and the player carries a warm lamp with a brighter core.
  ctx.globalCompositeOperation = 'lighter';
  const tint = (x: number, y: number, r: number, rgb: [number, number, number], a: number) => {
    const sx = (x - v.camX) * k;
    const sy = (y - v.camY) * k;
    const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * k);
    grad.addColorStop(0, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a.toFixed(3)})`);
    grad.addColorStop(1, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(sx - r * k, sy - r * k, r * 2 * k, r * 2 * k);
  };
  for (const light of lights) tint(light.x, light.y, Math.max(light.rx, light.ry) * 2.6 + 8, light.rgb, Math.min(0.2, light.alpha * 1.1 + 0.03));
  tint(v.you.x, v.you.y - 8, 150, [255, 214, 150], 0.06);
  tint(v.you.x, v.you.y - 8, 52, [255, 236, 200], 0.09);
  ctx.restore();
}
